// What to write, and what it will cost before it is pressed.
//
// The price is on the button because that is the only honest place for it. A
// credit balance in a corner tells you what you have; a number on the control
// tells you what this press takes, which is the question actually being asked.

import type { AiCreditCost, AiModel, DraftIntent } from "../lib/types";
import { Spinner } from "../components/Spinner";

const INTENTS: Array<{ key: DraftIntent; label: string; hint: string }> = [
  { key: "post", label: "Post", hint: "The whole thing — a few short paragraphs." },
  { key: "description", label: "Short description", hint: "A line or two under a photo." },
  { key: "punchline", label: "Punch line", hint: "One sentence that could stand alone." },
  { key: "freeform", label: "Anything", hint: "Ask for whatever you need." },
];

export function Composer({
  intent, onIntent, brief, onBrief, models, costs, modelKey, onModel,
  busy, disabled, onGenerate, credits,
}: {
  intent: DraftIntent;
  onIntent: (i: DraftIntent) => void;
  brief: string;
  onBrief: (b: string) => void;
  models: AiModel[];
  costs: AiCreditCost[];
  modelKey: string | null;
  onModel: (k: string) => void;
  busy: boolean;
  /** The plan or the role says no. The reason is shown by the screen, not here. */
  disabled: boolean;
  onGenerate: () => void;
  /** null when the plan is unlimited, or when the workspace brought its own key. */
  credits: number | null;
}) {
  const priceOf = (i: DraftIntent) =>
    costs.find((c) => c.intent === i && c.model_key === modelKey)?.credits ?? null;

  const price = priceOf(intent);
  const tooExpensive = credits !== null && price !== null && price > credits;

  return (
    <div className="card studio-composer">
      <div className="studio-intents" role="group" aria-label="What to write">
        {INTENTS.map((i) => {
          const p = priceOf(i.key);
          return (
            <button
              key={i.key}
              type="button"
              className={i.key === intent ? "chip ok" : "chip"}
              aria-pressed={i.key === intent}
              onClick={() => onIntent(i.key)}
              title={i.hint}
            >
              {i.label}
              {p !== null && <span className="studio-price">{p}</span>}
            </button>
          );
        })}
      </div>

      <p className="muted studio-hint">{INTENTS.find((i) => i.key === intent)?.hint}</p>

      <label htmlFor="brief">What is it about?</label>
      <textarea
        id="brief"
        rows={4}
        value={brief}
        placeholder="A new Thursday evening class for beginners. Small groups, first one free."
        onChange={(e) => onBrief(e.target.value)}
      />

      <div className="row studio-actions">
        <button
          className="primary"
          onClick={onGenerate}
          disabled={busy || disabled || !brief.trim() || tooExpensive}
        >
          {busy && <Spinner />} {busy ? "Writing…" : "Write it"}
        </button>

        {/* Shown only when there is a choice: one model is not a decision. */}
        {models.length > 1 && (
          <>
            <label className="visually-hidden" htmlFor="studio-model">Model</label>
            <select
              id="studio-model"
              value={modelKey ?? ""}
              onChange={(e) => onModel(e.target.value)}
              disabled={busy}
            >
              {models.map((m) => (
                <option key={m.key} value={m.key}>{m.display_name}</option>
              ))}
            </select>
          </>
        )}

        {tooExpensive && (
          <span className="muted">
            That costs {price} credits and you have {credits}.
          </span>
        )}
      </div>
    </div>
  );
}
